import { useMemo } from "react";
import { useActivationPacksForAgent } from "@/hooks/activation/useActivationPacks";
import { usePrintOrder } from "@/hooks/activation/usePrintOrder";
import { useUserAgents } from "@/hooks/agent/useAgents";
import { useActivities } from "@/hooks/analytics/useActivities";
import { useConversations } from "@/hooks/conversation/useConversations";
import { usePaginatedDocuments } from "@/hooks/documents/useDocumentFetch";
import { STALE_DOCUMENT_DAYS } from "@/pages/Dashboard/nextAction";
import { ACTIVITY_ACTIONS } from "@/services/analytics/ActivityService";
import { Agent } from "@/types/agent/agent";
import {
  BuildBandData,
  BuildStep,
  ConsoleBand,
  GenieBandData,
  GenieConsoleState,
  KnowledgeBandData,
  NextActionInput,
  PrintOrderState,
  ReachBandData,
  ReachChannel,
  UseGenieConsoleOptions,
  VoiceBandData,
} from "@/types/dashboard/genie-console";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

/** How many of the user's own items each band previews. */
export const CONSOLE_PREVIEW_LIMIT = 5;

/** Conversation window the VOICE band reads. */
const CONVERSATION_RANGE = "30d";

/** Genies fetched for the header switcher. Shared with useSelectedGenieId so
 *  both hooks land on the same React Query key and only one request is made. */
export const CONSOLE_GENIE_LIMIT = 50;

const asError = (error: unknown): Error | null =>
  error instanceof Error ? error : error ? new Error(String(error)) : null;

/**
 * Wrap one query into a band. `isLoading` is true only when there is nothing
 * cached to paint; once data exists the band stays on screen and revalidates
 * behind `isFetching` (React Query's stale-while-revalidate default, which the
 * rest of this app already relies on).
 */
const toBand = <TData,>(
  data: TData,
  query: {
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    error: unknown;
    isSuccess: boolean;
  },
  /** Bands whose inputs are missing resolve immediately with empty data. */
  skipped = false,
): ConsoleBand<TData> => ({
  data,
  isLoading: skipped ? false : query.isLoading,
  isFetching: skipped ? false : query.isFetching,
  isError: skipped ? false : query.isError,
  error: skipped ? null : asError(query.error),
  isSettled: skipped || query.isSuccess || query.isError,
});

const daysBetween = (from: Date, to: Date) =>
  Math.floor((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));

const isStale = (document: DocumentWithMetadata, now: Date): boolean => {
  const timestamp = document.updated_at || document.created_at;
  if (!timestamp) return false;
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return false;
  return daysBetween(parsed, now) > STALE_DOCUMENT_DAYS;
};

const publicUrlFor = (genie: Agent | null): string | null => {
  const urlName = genie?.url_name ?? genie?.page?.url_name;
  if (!urlName) return null;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/live-agent/${urlName}`;
};

const buildChannels = (genie: Agent | null, packCount: number): ReachChannel[] => {
  if (!genie) return [];
  const publicUrl = publicUrlFor(genie);
  const phoneNumber = genie.phone?.number ?? null;

  return [
    {
      id: "web",
      label: "Web page",
      genieIcon: "globe",
      live: Boolean(publicUrl),
      detail: publicUrl ? publicUrl.replace(/^https?:\/\//, "") : "No link name set",
    },
    {
      id: "qr",
      label: "QR code",
      genieIcon: "qr-code",
      live: Boolean(publicUrl),
      detail: publicUrl ? "Ready to scan" : "Needs a link name",
    },
    {
      id: "phone",
      label: "Phone",
      genieIcon: "phone",
      live: Boolean(phoneNumber),
      detail: phoneNumber ?? "Not connected",
    },
    {
      id: "embed",
      label: "Website widget",
      genieIcon: "code",
      live: Boolean(publicUrl),
      detail: publicUrl ? "Embed code ready" : "Needs a link name",
    },
    {
      id: "pack",
      label: "Activation pack",
      genieIcon: "share",
      live: packCount > 0,
      detail: packCount > 0 ? `${packCount} built` : "None yet",
    },
  ];
};

const buildSteps = (
  genie: Agent | null,
  documentCount: number,
  packCount: number,
  conversationCount: number,
): BuildStep[] => {
  if (!genie) return [];
  const welcome = genie.welcome_message ?? genie.page?.welcome_message ?? null;

  return [
    {
      id: "identity",
      label: "Named and described",
      done: Boolean(genie.name && genie.description),
      detail: genie.description ? genie.description.slice(0, 60) : null,
    },
    {
      id: "voice",
      label: "Voice chosen",
      done: Boolean(genie.voice),
      detail: genie.voice_label ?? null,
    },
    {
      id: "welcome",
      label: "Welcome message written",
      done: Boolean(welcome),
      detail: welcome ? welcome.slice(0, 60) : null,
    },
    {
      id: "knowledge",
      label: "Knowledge attached",
      done: documentCount > 0,
      detail: documentCount > 0 ? `${documentCount} documents` : null,
    },
    {
      id: "leads",
      label: "Capturing details",
      done: Boolean(genie.capture_leads),
      detail: genie.capture_leads ? "Lead capture on" : null,
    },
    {
      id: "reach",
      label: "Out in the world",
      done: packCount > 0,
      detail: packCount > 0 ? `${packCount} activation packs` : null,
    },
    {
      id: "live",
      label: "Talking to customers",
      done: conversationCount > 0,
      detail: conversationCount > 0 ? `${conversationCount} conversations` : null,
    },
  ];
};

/**
 * The dashboard console (HG-1228).
 *
 * Assembles the genie / knowledge / reach / voice / build bands out of the
 * hooks the rest of the app already uses, and exposes the flat inputs the
 * "do this next" ranking consumes. Every band resolves independently: a band
 * that is loading, empty, or failed renders its own state without blanking
 * its neighbours, and every field is defensive against missing data.
 */
export const useGenieConsole = (
  agentId: string | null | undefined,
  options: UseGenieConsoleOptions = {},
): GenieConsoleState => {
  const resolvedAgentId = agentId || null;

  // --- genie band -----------------------------------------------------
  const geniesQuery = useUserAgents(CONSOLE_GENIE_LIMIT);
  const genies = useMemo<Agent[]>(
    () => (geniesQuery.data as Agent[] | undefined) ?? [],
    [geniesQuery.data],
  );
  const selectedGenie = useMemo(
    () => genies.find((genie) => genie.id === resolvedAgentId) ?? null,
    [genies, resolvedAgentId],
  );

  const genieBand = toBand<GenieBandData>(
    { genies, selectedGenie, genieCount: genies.length },
    geniesQuery,
  );

  // --- knowledge band -------------------------------------------------
  const documentsQuery = usePaginatedDocuments("my-documents", CONSOLE_PREVIEW_LIMIT, {
    agentId: resolvedAgentId ?? undefined,
  });

  const knowledgeData = useMemo<KnowledgeBandData>(() => {
    const pages = documentsQuery.data?.pages ?? [];
    const documents = pages.flatMap((page) => page?.data ?? []);
    const now = new Date();
    const timestamps = documents
      .map((document) => document.updated_at || document.created_at)
      .filter((value): value is string => Boolean(value))
      .sort();

    return {
      documents,
      documentCount: pages[0]?.total ?? documents.length,
      staleDocuments: documents.filter((document) => isStale(document, now)),
      lastUpdatedAt: timestamps.length ? timestamps[timestamps.length - 1] : null,
    };
  }, [documentsQuery.data]);

  const knowledgeBand = toBand<KnowledgeBandData>(knowledgeData, documentsQuery);

  // --- reach band -----------------------------------------------------
  const packsQuery = useActivationPacksForAgent(resolvedAgentId ?? "");
  const packs = useMemo(() => packsQuery.data ?? [], [packsQuery.data]);
  const firstPackId = packs[0]?.id ?? null;

  // Read-only by default: the only print-order endpoint available to the
  // client inserts a draft row, so the dashboard must not call it on load.
  // See docs/handoff/dashboard-print-order-read.md.
  const printOrderEnabled = Boolean(options.enablePrintOrder && firstPackId);
  const printOrderQuery = usePrintOrder(printOrderEnabled ? (firstPackId as string) : "");

  const scansQuery = useActivities(
    {
      action: ACTIVITY_ACTIONS.QR_SCANNED,
      resourceType: "genie",
      resourceId: resolvedAgentId ?? undefined,
    },
    { enabled: Boolean(resolvedAgentId) },
  );

  const reachData = useMemo<ReachBandData>(() => {
    const printOrderStatus: PrintOrderState = !printOrderEnabled
      ? "unknown"
      : printOrderQuery.data?.status ?? (packs.length ? "none" : "unknown");

    return {
      packs,
      packCount: packs.length,
      publicUrl: publicUrlFor(selectedGenie),
      qrValue: publicUrlFor(selectedGenie) ? `${publicUrlFor(selectedGenie)}?ref=qr` : null,
      channels: buildChannels(selectedGenie, packs.length),
      scanCount: scansQuery.data?.pages?.[0]?.count ?? 0,
      printOrderStatus,
    };
  }, [packs, selectedGenie, scansQuery.data, printOrderEnabled, printOrderQuery.data]);

  const reachBand = toBand<ReachBandData>(
    reachData,
    {
      isLoading: packsQuery.isLoading || scansQuery.isLoading,
      isFetching: packsQuery.isFetching || scansQuery.isFetching,
      isError: packsQuery.isError || scansQuery.isError,
      error: packsQuery.error ?? scansQuery.error,
      isSuccess: packsQuery.isSuccess && scansQuery.isSuccess,
    },
    !resolvedAgentId,
  );

  // --- voice band -----------------------------------------------------
  const conversationsQuery = useConversations(
    resolvedAgentId ? [resolvedAgentId] : [],
    CONVERSATION_RANGE,
    true,
  );

  const voiceData = useMemo<VoiceBandData>(() => {
    const conversations = (conversationsQuery.data?.pages ?? []).flatMap(
      (page) => page?.data ?? [],
    );
    const firstDate = conversations
      .map((conversation) => conversation.details?.conversation_date ?? null)
      .find((value): value is string => Boolean(value));

    return {
      conversations: conversations.slice(0, CONSOLE_PREVIEW_LIMIT),
      conversationCount: conversations.length,
      unreviewedCount: conversations.filter((conversation) => conversation.viewed !== true).length,
      lastConversationAt: firstDate ?? null,
    };
  }, [conversationsQuery.data]);

  const voiceBand = toBand<VoiceBandData>(voiceData, conversationsQuery, !resolvedAgentId);

  // --- build band -----------------------------------------------------
  // Derived from bands already on screen — no extra request, and it inherits
  // the genie band's own loading/error state.
  const buildData = useMemo<BuildBandData>(() => {
    const steps = buildSteps(
      selectedGenie,
      knowledgeData.documentCount,
      reachData.packCount,
      voiceData.conversationCount,
    );

    return {
      steps,
      completedCount: steps.filter((step) => step.done).length,
      totalCount: steps.length,
      otherGenies: genies.filter((genie) => genie.id !== resolvedAgentId),
    };
  }, [
    selectedGenie,
    knowledgeData.documentCount,
    reachData.packCount,
    voiceData.conversationCount,
    genies,
    resolvedAgentId,
  ]);

  const buildBand = toBand<BuildBandData>(buildData, geniesQuery);

  // --- ranking inputs -------------------------------------------------
  const ranking = useMemo<NextActionInput>(
    () => ({
      // The genie band always has to answer. The rest only gate readiness
      // once we actually have a genie to ask about.
      isReady:
        genieBand.isSettled &&
        (genies.length === 0 ||
          (knowledgeBand.isSettled && reachBand.isSettled && voiceBand.isSettled)),
      genieCount: genies.length,
      genieId: selectedGenie?.id ?? null,
      genieName: selectedGenie?.name ?? null,
      documentCount: knowledgeData.documentCount,
      staleDocumentCount: knowledgeData.staleDocuments.length,
      unreviewedConversationCount: voiceData.unreviewedCount,
      packCount: reachData.packCount,
      firstPackId,
      printOrderStatus: reachData.printOrderStatus,
      scanCount: reachData.scanCount,
    }),
    [
      genieBand.isSettled,
      knowledgeBand.isSettled,
      reachBand.isSettled,
      voiceBand.isSettled,
      genies.length,
      selectedGenie,
      knowledgeData,
      voiceData,
      reachData,
      firstPackId,
    ],
  );

  return {
    agentId: resolvedAgentId,
    genie: genieBand,
    knowledge: knowledgeBand,
    reach: reachBand,
    voice: voiceBand,
    build: buildBand,
    ranking,
  };
};
